import { useQuery } from '@tanstack/react-query';

import { donedealServicesGateway } from '../../../../api/api-utils/commonApiUtils';

import type {
  Filters,
  MakeModelOption,
} from '../../MakeModelKeywordSearch.typed';
import { z } from 'zod';
import { useMemo } from 'react';
import { formatNumber } from '../../../../helpers/formatting';

type Data = Array<MakeModelOption>;

/**
 * @param args filter values to be included in search payload
 * @returns autocomplete options, error state and loading state due to network requests
 */
export const useMakeModelKeywordSearch = ({
  maxPrice,
  minPrice,
  maxYear,
  minYear,
  keyword,
}: Filters & { keyword: string }): {
  isPending: boolean;
  data: Data | undefined;
  isError: boolean;
} => {
  const { data, isPending, isError } = useQuery({
    queryKey: [
      'makeModelKeywordSearch',
      { minPrice, maxPrice, minYear, maxYear, keyword },
    ],
    queryFn: () =>
      fetchMakeAndModels({ minPrice, maxPrice, minYear, maxYear, keyword }),
    enabled: Boolean(keyword),
    retry: false,
  });

  return useMemo(
    () => ({
      isPending,
      data: data?.map((result) => ({
        label: result.displayName,
        value: { make: result.make, model: result.model ?? undefined },
        count: String(formatNumber(result.count)),
      })),
      isError,
    }),
    [data, isPending, isError],
  );
};

const responseSchema = z.object({
  results: z.array(
    z.object({
      make: z.string(),
      model: z.string().nullable(),
      displayName: z.string(),
      count: z.number(),
    }),
  ),
});

async function fetchMakeAndModels({
  maxPrice,
  minPrice,
  maxYear,
  minYear,
  keyword,
}: Filters & { keyword: string }) {
  const ranges = [];

  if (maxPrice !== undefined || minPrice !== undefined) {
    ranges.push({
      name: 'price',
      ...(minPrice !== undefined && { from: minPrice }),
      ...(maxPrice !== undefined && { to: maxPrice }),
    });
  }

  if (maxYear !== undefined || minYear !== undefined) {
    ranges.push({
      name: 'year',
      ...(minYear !== undefined && { from: minYear }),
      ...(maxYear !== undefined && { to: maxYear }),
    });
  }

  const response = await donedealServicesGateway.post(
    '/v4/filters/search/text',
    {
      text: keyword,
      searchApiRequest: {
        filters: [],
        ranges,
      },
    },
  );

  return responseSchema.parse(response.data).results;
}
